import { IconDot } from "@/assets/icons/geist/IconDot";
import type { Size } from "@/assets/icons/utils";
import { cn, type PropsWithClassName } from "@/components/podkit/lib/cn";
import { Tooltip } from "@/components/Tooltip";
import { type PlainEnvironment } from "@/queries/environment-queries";
import { effectiveState } from "@/routes/environments/phase";
import { statusText } from "@/utils/environment";
import { EnvironmentPhase } from "gitpod-next-api/gitpod/v1/environment_pb";
import { useMemo, type FC } from "react";

type EnvironmentStatusDotProps = {
    env: PlainEnvironment;
    size: Size;
} & PropsWithClassName;

export const EnvironmentStatusDot: FC<EnvironmentStatusDotProps> = ({ env, size, className }) => {
    const state = useMemo(() => effectiveState(env), [env]);
    const tooltip = useMemo(() => statusText(state), [state]);

    const { color } = useMemo(() => {
        let color = cn("text-content-tertiary [&_circle]:!stroke-transparent");

        switch (state.state) {
            case EnvironmentPhase.RUNNING:
                color = cn("text-content-green");
                break;
            case EnvironmentPhase.UPDATING:
            case EnvironmentPhase.STARTING:
            case EnvironmentPhase.CREATING:
                color = cn("text-content-brand");
                break;
            case EnvironmentPhase.STOPPING:
            case EnvironmentPhase.DELETING:
                color = cn("text-content-brand animate-pulse");
                break;
            case EnvironmentPhase.STOPPED:
                if (state.failures) {
                    color = cn("text-content-red");
                } else {
                    color = cn("text-content-tertiary [&_circle]:!stroke-transparent");
                }
                break;
        }
        return {
            color,
            ...state,
        };
    }, [state]);

    return (
        <Tooltip content={tooltip} usePortal>
            <IconDot
                aria-label={tooltip}
                size={size}
                className={cn("block", color, className)}
                data-testid="environment-status-dot"
            />
        </Tooltip>
    );
};
